import React, {useState} from "react";
import {Button, Form} from "react-bootstrap";
import {useMutation, useQuery} from "react-query";
import {getActivity} from "Api/Query/ActivityQuery";
import {addServiceToInscriptionOrderActivity} from "Api/Mutation/InscriptionOrder";
import {ACTIVITY_TYPE_YEARLY_ID} from "Components/Activities/ActivityTypeSelect";
import SmallModal from "Components/Modal/SmallModal";
import {Notificator} from "Services/Notificator/Notificator";
import {ActivityYearDay} from "Model/Activity/ActivityYearDay";
import {ActivityService} from "Model/Activity/ActivityService";

interface Props {
    inscriptionOrderActivity: any
    onAdded: () => void
}

export const AddServiceToActivityModal: React.FC<Props> = ({inscriptionOrderActivity, onAdded}) => {

    const activityId: string | undefined = inscriptionOrderActivity.activity?.id

    const [show, setShow] = useState<boolean>(false)
    const [selectedDayId, setSelectedDayId] = useState<string>('')
    const [selectedServiceId, setSelectedServiceId] = useState<string>('')

    const activityQuery = useQuery({
        queryKey: ['getActivity', activityId ?? ''],
        queryFn: () => getActivity(activityId ?? ''),
        enabled: !!activityId
    })

    const activity: any = (activityQuery.data as any)?.data?._result

    const addServiceMutation = useMutation({
        mutationKey: ['addServiceToInscriptionOrderActivity', inscriptionOrderActivity.id],
        mutationFn: () => addServiceToInscriptionOrderActivity(
            inscriptionOrderActivity.id,
            selectedDayId,
            selectedServiceId
        ),
        onSuccess: () => {
            Notificator.success('El servicio se ha añadido correctamente.')
            handleClose()
            onAdded()
        },
        onError: () => {
            Notificator.error('Se ha producido un error al añadir el servicio.', 'Error')
        }
    })

    const handleClose = () => {
        setShow(false)
        setSelectedDayId('')
        setSelectedServiceId('')
    }

    // Solo se muestra para actividades anuales (early return tras todos los hooks)
    if (!activity || activity.type !== ACTIVITY_TYPE_YEARLY_ID) {
        return null
    }

    const yearDays: ActivityYearDay[] = activity.activityYearDays ?? []

    const selectedDay: ActivityYearDay | undefined = yearDays.find(d => d.id === selectedDayId)

    const centerIdOfChild: string | undefined = inscriptionOrderActivity.center?.id

    const availableServices: ActivityService[] = selectedDay?.services
        ? selectedDay.services.filter(
            (s: ActivityService) => !centerIdOfChild || s.center?.id === centerIdOfChild
        )
        : []

    const handleConfirm = () => {
        if (!selectedDayId) return
        addServiceMutation.mutate()
    }

    const canConfirm = !!selectedDayId

    const modalBody = (
        <div className={"p-2"}>
            <h5 className={"mb-4"}>Añadir día o servicio</h5>

            <Form.Group className={"mb-3"}>
                <Form.Label className={"fw-bold"}>Día</Form.Label>
                <Form.Select
                    value={selectedDayId}
                    onChange={(e) => {
                        setSelectedDayId(e.target.value)
                        setSelectedServiceId('')
                    }}
                >
                    <option value={""}>-- Selecciona un día --</option>
                    {yearDays.map((day: ActivityYearDay) => (
                        <option key={day.id} value={day.id}>{day.name}</option>
                    ))}
                </Form.Select>
            </Form.Group>

            <Form.Group className={"mb-3"}>
                <Form.Label className={"fw-bold"}>Servicio</Form.Label>
                <Form.Select
                    value={selectedServiceId}
                    onChange={(e) => setSelectedServiceId(e.target.value)}
                    disabled={!selectedDayId}
                >
                    <option value={""}>-- Selecciona un servicio --</option>
                    {availableServices.map((service: ActivityService) => (
                        <option key={service.id} value={service.id}>
                            {service.name}
                        </option>
                    ))}
                </Form.Select>
                {selectedDayId && availableServices.length === 0 && (
                    <Form.Text className={"text-muted"}>
                        No hay servicios disponibles para este día.
                    </Form.Text>
                )}
            </Form.Group>
        </div>
    )

    return (
        <>
            <Button
                size="sm"
                variant={"secondary"}
                className={"ms-2"}
                onClick={() => setShow(true)}
            >
                Añadir día o servicio
            </Button>

            <SmallModal
                show={show}
                body={modalBody}
                onClose={handleClose}
                onConfirm={canConfirm ? handleConfirm : undefined}
                confirmText={"Añadir día o servicio"}
                isConfirmLoading={addServiceMutation.isLoading}
            />
        </>
    )
}
